import { useEffect, type FC } from 'react';
import { useForm } from '@mantine/form';
import { Badge, Button, Group, LoadingOverlay, Stack } from '@mantine/core';
import {
    formatPayinTrader,
    PayinTraderStatus,
    payinTraderStatusColors,
    useActivatePayinTrader,
    useBlockPayinTrader,
    usePayinTrader,
    useUnBlockPayinTrader,
    useUpdatePayinTraderConfig,
} from '~/entities/payin';
import { KeyValuePair } from '~/shared/ui/KeyValuePair';
import { transformValues } from '../config';
import { type ConfigForm } from '../model';
import { ActivationForm } from './ActivationForm';
import { ConfigEditor } from './ConfigEditor';

interface TraderEditorProps {
    id: string;
    onSuccess?: () => void;
    onError?: () => void;
}

export const TraderEditor: FC<TraderEditorProps> = ({ id }) => {
    const traderRes = usePayinTrader({ 'traderId.value': id });
    const trader = traderRes.data?.trader;

    const form = useForm<ConfigForm>({
        initialValues: { config: trader?.config, traderId: { value: id } },
        transformValues,
    });
    const editTraderConfig = useUpdatePayinTraderConfig();

    const block = useBlockPayinTrader();
    const unBlock = useUnBlockPayinTrader();
    const activate = useActivatePayinTrader();

    const traderId = trader?.id?.value;

    useEffect(() => {
        if (trader) {
            form.setValues({
                config: trader.config,
                traderId: trader.id,
            });
            form.resetDirty();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trader]);

    return (
        <>
            <LoadingOverlay visible={traderRes.isPending} />

            <Stack align='stretch'>
                <KeyValuePair
                    label='ID'
                    value={trader?.id?.value}
                />

                <KeyValuePair
                    label='UserID'
                    value={trader?.userId?.value}
                />

                {trader?.traderName?.value && (
                    <KeyValuePair
                        label='Имя трейдера'
                        value={trader?.traderName?.value}
                    />
                )}

                <KeyValuePair
                    label='Имя пользователя'
                    value={trader?.username}
                />

                {trader?.traderName?.value && (
                    <KeyValuePair
                        label='Имя трейдера'
                        value={trader?.traderName?.value}
                    />
                )}

                <KeyValuePair
                    label='Статус'
                    value={
                        <Badge
                            fz='xs'
                            color={payinTraderStatusColors[trader?.status as PayinTraderStatus]}
                        >
                            {formatPayinTrader.status(trader?.status)}
                        </Badge>
                    }
                />

                {/* <KeyValuePair
                    label='Статус работы'
                    value={
                        <Badge
                            fz='xs'
                            color={payinTraderWorkingStatusColors[trader?.workingStatus as PayinTraderWorkingStatus]}
                        >
                            {formatPayinTrader.workingStatus(trader?.workingStatus)}
                        </Badge>
                    }
                /> */}

                {trader && trader?.status !== PayinTraderStatus.STATUS_CREATED && (
                    <form
                        onSubmit={form.onSubmit((v) => {
                            editTraderConfig.mutate(v);
                            form.resetDirty();
                        })}
                    >
                        <Stack gap='xs'>
                            <ConfigEditor
                                form={form}
                                isLoading={editTraderConfig.isPending}
                            />

                            <Button
                                fullWidth
                                type='submit'
                                disabled={editTraderConfig.isPending}
                                loading={editTraderConfig.isPending}
                            >
                                Сохранить
                            </Button>
                        </Stack>
                    </form>
                )}

                {traderId && (
                    <Stack>
                        {trader.status === PayinTraderStatus.STATUS_CREATED && (
                            <ActivationForm
                                traderId={traderId}
                                isLoading={activate.isPending}
                                onSubmit={(val) => {
                                    activate.mutate(val);
                                }}
                            />
                        )}

                        <Group justify='flex-end'>
                            {trader?.status === PayinTraderStatus.STATUS_ACTIVE && (
                                <Button
                                    onClick={() =>
                                        block.mutate({
                                            traderId: {
                                                value: traderId,
                                            },
                                        })
                                    }
                                    color='red'
                                    disabled={block.isPending}
                                    loading={block.isPending}
                                >
                                    Заблокировать
                                </Button>
                            )}

                            {trader.status === PayinTraderStatus.STATUS_BLOCKED && (
                                <Button
                                    onClick={() =>
                                        unBlock.mutate({
                                            traderId: {
                                                value: traderId,
                                            },
                                        })
                                    }
                                    variant='light'
                                    disabled={unBlock.isPending}
                                    loading={unBlock.isPending}
                                >
                                    Разблокировать
                                </Button>
                            )}
                        </Group>
                    </Stack>
                )}
            </Stack>
        </>
    );
};
